import settings from 'Cloud/settings';
import { IS_AUTO_TEST_HIDE } from 'reactApp/appHelpers/configHelpers';
import { overquotaDebugLogger } from 'reactApp/appHelpers/featuresHelpers/features/overquotaStates';
import { isPublicPasswordLinkEnabled, isThemedPublicLinkEnabled } from 'reactApp/appHelpers/featuresHelpers/features/promoLinks';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getCurrentStorage, isReactLandingPage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { ConditionsCheckType, getCheckAllConditions } from 'reactApp/utils/conditionChecker';
import { put } from 'redux-saga/effects';
import type { QuotaStates } from 'server/types/context/IUser';
import { call, select } from 'typed-redux-saga';

const checkAllConditionsFalse = getCheckAllConditions(overquotaDebugLogger, ConditionsCheckType.FirstTrue);

const QUOTA_TIP_ID = 'overQuotaTip';

export function* checkEnvironmentOverquotaConditions(
    { state }: { state?: QuotaStates },
    {
        isPublicPasswordLink = isPublicPasswordLinkEnabled,
        isThemedPublicLink = isThemedPublicLinkEnabled,
        isAutoTestHide = IS_AUTO_TEST_HIDE,
    } = {
        isPublicPasswordLink: isPublicPasswordLinkEnabled,
        isThemedPublicLink: isThemedPublicLinkEnabled,
        isAutoTestHide: IS_AUTO_TEST_HIDE,
    }
) {
    const { action = null } = yield* select(SettingsSelectors.getQueryParams);
    const storage = yield* select(getCurrentStorage);
    const { isHome, isPrivatePage, isAllDocuments } = getStorage(storage);
    const isWebview = yield* select(EnvironmentSelectors.isWebview);
    const isLP = yield* call(isReactLandingPage);
    const isPhone = yield* select(EnvironmentSelectors.isPhone);
    const isViewerActive = yield* select(ViewerSelectors.isViewerActive);
    const stateCleared = state === 'cleared' || state === 'after_clear';

    let isOk = false;

    isOk = checkAllConditionsFalse('checkStopOverquotaProcessing (isViewer check)', {
        isViewerActive,
    });

    if (!isOk) {
        return false;
    }

    if (stateCleared) {
        return true;
    }

    // дальше проверки только для не cleared/after_clear состояний

    isOk = checkAllConditionsFalse('checkStopOverquotaProcessing (action param checks)', {
        requestPaymentAction: { check: action === 'request-payment', state, action },
        publicPasswordLinkAction: { check: isPublicPasswordLink, state, action },
        themedPublicLinkEnabled: { check: isThemedPublicLink, state, action },
    });

    if (!isOk) {
        return false;
    }

    if (isPhone) {
        isOk = checkAllConditionsFalse('checkStopOverquotaProcessing (mobile checks)', {
            webview: { check: isWebview, state, isWebview },
            settingsRequestAction: {
                check: settings?.request?.action,
                state,
                action: settings?.request?.action,
            },
            mobileLandingOrSubsOrFamily: {
                check: isLP || storage === EStorageType.subscriptions || storage === EStorageType.family,
                state,
                storage,
                isLP,
            },
        });

        if (!isOk) {
            return false;
        }
    }

    if (!isPhone) {
        // Форс-включение промки для отдельных автотестов.
        const force = yield put(UserStorageActions.get(`force_${QUOTA_TIP_ID}`)) || false;

        const showOverquotaInPrivatePages = isHome || (isPrivatePage && state === 'full_blocked' && !isAllDocuments);

        isOk = checkAllConditionsFalse('checkStopOverquotaProcessing (desktop checks)', {
            notShowInPrivatePages: {
                check: !showOverquotaInPrivatePages,
                state,
                isPhone,
                showOverquotaInPrivatePages: {
                    storage,
                    isPrivatePage,
                },
            },
            autoTestHide: {
                check: isAutoTestHide && !force,
                state,
                isPhone,
                isAutoTestHide,
                force,
            },
        });

        if (!isOk) {
            return false;
        }
    }

    return true;
}
